import { useState } from "react"
import {
  DEFAULT_SETTINGS,
  useSettings,
  type BattlePace,
  type MotionLevel,
  type TextScale,
} from "../game/settings"
import { clearHistory } from "../game/historyLog"
import { clearAnalytics, getAnalyticsSummary } from "../game/analytics"

interface SettingsScreenProps {
  onBack: () => void
}

/** A labelled row of mutually-exclusive choices, drawn as a segmented control. */
function Choice<T extends string>({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string
  hint?: string
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (value: T) => void
}) {
  return (
    <div className="setting-row">
      <div className="setting-label">
        <span className="setting-title">{label}</span>
        {hint && <span className="setting-hint">{hint}</span>}
      </div>
      <div className="setting-segments" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={option.value === value ? "selected" : ""}
            aria-pressed={option.value === value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

const TEXT_SCALE_OPTIONS: Array<{ value: TextScale; label: string }> = [
  { value: "small", label: "Small" },
  { value: "normal", label: "Normal" },
  { value: "large", label: "Large" },
  { value: "xlarge", label: "Larger" },
]

const MOTION_OPTIONS: Array<{ value: MotionLevel; label: string }> = [
  { value: "full", label: "Full" },
  { value: "reduced", label: "Reduced" },
  { value: "none", label: "Off" },
]

const BATTLE_PACE_OPTIONS: Array<{ value: BattlePace; label: string }> = [
  { value: "instant", label: "Instant" },
  { value: "brisk", label: "Brisk" },
  { value: "measured", label: "Measured" },
]

const TOGGLE_OPTIONS: Array<{ value: "on" | "off"; label: string }> = [
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
]

export function SettingsScreen({ onBack }: SettingsScreenProps) {
  const [settings, update] = useSettings()
  // A one-shot line confirming a destructive maintenance action fired, since
  // clearing a log otherwise gives no sign it did anything.
  const [notice, setNotice] = useState<string | null>(null)
  // Recomputed each render, so the numbers refresh after a clear.
  const summary = getAnalyticsSummary()

  return (
    <div className="screen settings-screen">
      <h1>Settings</h1>
      <p className="journal-intro">
        How the game is shown, and what it is worth knowing about how you play.
      </p>

      <h2>Presentation</h2>
      <Choice
        label="Text size"
        hint="Scales all text and spacing."
        value={settings.textScale}
        options={TEXT_SCALE_OPTIONS}
        onChange={(textScale) => update({ textScale })}
      />
      <Choice
        label="Animation"
        hint="Reduced trims flourishes; Off stills them. Your system's reduce-motion setting is always honoured."
        value={settings.motion}
        options={MOTION_OPTIONS}
        onChange={(motion) => update({ motion })}
      />
      <Choice
        label="Battle speed"
        hint="How long the field holds on your blow before the enemy answers it."
        value={settings.battlePace}
        options={BATTLE_PACE_OPTIONS}
        onChange={(battlePace) => update({ battlePace })}
      />

      <h2>Usability</h2>
      <Choice
        label="Skip read dialogue"
        hint="Automatically pass over a conversation you have already read to its end."
        value={settings.autoSkipSeenDialogue ? "on" : "off"}
        options={TOGGLE_OPTIONS}
        onChange={(value) => update({ autoSkipSeenDialogue: value === "on" })}
      />
      <Choice
        label="Confirm long actions"
        hint={`Ask before an action that would spend ${settings.longActionThreshold} days or more at once.`}
        value={settings.confirmLongActions ? "on" : "off"}
        options={TOGGLE_OPTIONS}
        onChange={(value) => update({ confirmLongActions: value === "on" })}
      />

      <h2>Your Chronicle</h2>
      <p className="settings-note">
        Kept on this device only. Nothing leaves your browser unless a collection
        point was configured when this build was made.
      </p>
      <dl className="status-line analytics-summary">
        <dt>Loops begun</dt>
        <dd>{summary.loops}</dd>
        <dt>Deaths</dt>
        <dd>{summary.deaths}</dd>
        <dt>Reached Day 30</dt>
        <dd>{summary.day30Survivals} time{summary.day30Survivals === 1 ? "" : "s"}</dd>
        <dt>Abilities raised</dt>
        <dd>{summary.abilityProgressions}</dd>
        <dt>Factions settled</dt>
        <dd>{summary.factionCompletions}</dd>
        <dt>Battles fought</dt>
        <dd>{summary.battlesFought}</dd>
        <dt>Battles lost</dt>
        <dd>
          {summary.battlesLost}
          {summary.battlesFought > 0 &&
            ` (${Math.round(summary.battleFailureRate * 100)}%)`}
        </dd>
        <dt>Longest survival</dt>
        <dd>{summary.longestSurvival} days</dd>
        <dt>Campaigns won</dt>
        <dd>{summary.completions}</dd>
      </dl>

      {notice && <p className="settings-note settings-notice">{notice}</p>}

      <div className="settings-actions">
        <button
          type="button"
          onClick={() => {
            update({ ...DEFAULT_SETTINGS })
            setNotice("Settings restored to their defaults.")
          }}
        >
          Restore Defaults
        </button>
        <button
          type="button"
          onClick={() => {
            clearHistory()
            setNotice("Event history cleared.")
          }}
        >
          Clear Event History
        </button>
        <button
          type="button"
          className="danger"
          onClick={() => {
            clearAnalytics()
            setNotice("Play statistics cleared.")
          }}
        >
          Clear Statistics
        </button>
      </div>

      <button type="button" onClick={onBack}>
        Back
      </button>
    </div>
  )
}
